import { useCallback, useEffect, useRef, useState } from "react";

import { getEnforcingRules, getRulesOnBreakPause, getRulesOnForcedStop } from "./ruleSchedule";
import type { Rule } from "./ruleTypes";

const SLOW_TICK_MS = 60_000;
const FAST_TICK_MS = 1_000;

/** Tick interval for enforcement countdowns — exported for unit tests. */
export function enforcementClockTickDelayMs(rules: Rule[] | undefined, now: Date = new Date()): number {
  const hasEnabledRule = rules?.some((rule) => rule.enabled) ?? false;
  const isEnforcing = rules !== undefined && getEnforcingRules(rules, now).length > 0;
  const onBreakPause = rules !== undefined && getRulesOnBreakPause(rules, now).length > 0;
  const onForcedStop = rules !== undefined && getRulesOnForcedStop(rules, now).length > 0;
  return isEnforcing || onBreakPause || onForcedStop || hasEnabledRule ? FAST_TICK_MS : SLOW_TICK_MS;
}

/**
 * Shared device clock for enforcement visibility and countdowns.
 * Ticks every second while rules may be enforcing (or an enabled rule could
 * activate soon); otherwise every 60s to catch scheduled window boundaries.
 */
export function useEnforcementClock(rules: Rule[] | undefined) {
  const [now, setNow] = useState(() => new Date());
  const rulesRef = useRef(rules);
  rulesRef.current = rules;

  const refreshNow = useCallback(() => setNow(new Date()), []);

  useEffect(() => {
    let timeoutId: number;

    const scheduleNext = () => {
      const current = new Date();
      setNow(current);
      const delay = enforcementClockTickDelayMs(rulesRef.current, current);
      timeoutId = window.setTimeout(scheduleNext, delay);
    };

    scheduleNext();
    return () => window.clearTimeout(timeoutId);
  }, [rules]);

  return { now, refreshNow };
}
